import { useCallback, useMemo, useRef, useState } from 'react';

import {
  GeneratedCreateLinkedDrawEntryIntentMutation,
  GeneratedCustomAttributeInput,
  GeneratedLinkedDrawEntryIntentLineItemInput,
  useCreateLinkedDrawEntryIntentMutation,
} from '../../../types/generated';
import { captureError } from '../../../utils/capture';

export type LinkedDrawEntryIntent = NonNullable<
  GeneratedCreateLinkedDrawEntryIntentMutation['createLinkedDrawEntryIntent']
>['intent'];

export type LinkedDrawEntryIntentInput = {
  multisetID: string;
  currencyCode: string;
  maxQuantity: number;
  shippingAddressId?: string | null;
  paymentMethodId?: string | null;
  preOrder?: boolean;
  authorizePayableAmount?: boolean;
  lineItems: GeneratedLinkedDrawEntryIntentLineItemInput[];
  customAttributes?: GeneratedCustomAttributeInput[];
};

export type LinkedDrawEntryIntentController = {
  clientSecret: string | null;
  error: boolean;
  intent: LinkedDrawEntryIntent | null;
  loading: boolean;
  reset: () => void;
  submit: (
    input: LinkedDrawEntryIntentInput,
  ) => Promise<LinkedDrawEntryIntent | null>;
  submittedInput: LinkedDrawEntryIntentInput | null;
};

export const useLinkedDrawEntryIntent = (): LinkedDrawEntryIntentController => {
  const [mutateCreate] = useCreateLinkedDrawEntryIntentMutation();

  const [intent, setIntent] = useState<LinkedDrawEntryIntent | null>(null);
  const [submittedInput, setSubmittedInput] =
    useState<LinkedDrawEntryIntentInput | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestRef = useRef(0);

  const reset = useCallback(() => {
    requestRef.current += 1;
    setIntent(null);
    setSubmittedInput(null);
    setClientSecret(null);
    setLoading(false);
    setError(false);
  }, []);

  const submit = useCallback(
    async (input: LinkedDrawEntryIntentInput) => {
      requestRef.current += 1;
      const requestId = requestRef.current;

      setLoading(true);
      setError(false);

      try {
        const { data, errors } = await mutateCreate({
          variables: {
            multisetID: input.multisetID,
            currencyCode: input.currencyCode,
            maxQuantity: input.maxQuantity,
            shippingAddressId: input.shippingAddressId ?? null,
            paymentMethodId: input.paymentMethodId ?? null,
            preOrder: input.preOrder ?? false,
            authorizePayableAmount: input.authorizePayableAmount ?? true,
            lineItems: input.lineItems,
            customAttributes: input.customAttributes ?? [],
          },
        });

        const response = data?.createLinkedDrawEntryIntent;
        if (!response || errors) {
          throw new Error(
            `Unable to create linked draw entry intent multiset:${input.multisetID}`,
          );
        }

        if (requestId !== requestRef.current) {
          return response.intent;
        }

        setIntent(response.intent);
        setSubmittedInput(input);
        setClientSecret(response.clientSecret);

        return response.intent;
      } catch (submitError) {
        if (submitError instanceof Error) captureError(submitError);
        if (requestId === requestRef.current) setError(true);

        return null;
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [mutateCreate],
  );

  return useMemo(
    () => ({
      clientSecret,
      error,
      intent,
      loading,
      reset,
      submit,
      submittedInput,
    }),
    [clientSecret, error, intent, loading, reset, submit, submittedInput],
  );
};
